import { palette } from '@/theme/constants';
import { Avatar, Box, CircularProgress, Theme, Typography, useMediaQuery } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { authSelector, collaboratorSelector, storySelector } from '@/store/selectors';
import { FC, useEffect, useState } from 'react';
import { MuiSelect } from '@/components';
import { getCollaboratorStory, updateCollaborator } from '@/store/collaborator/action';
import { UseFirstRender } from '@/hooks';
import { cdn_url } from '@/utils';

type Collaborator = {
  id: number;
  name: string;
  type: string;
  role: string;
};

export const Form: FC<any> = ({ formRef, onClose, extendedPalette }) => {
  const { t } = useTranslation();
  const { story } = useSelector(storySelector);
  const { user } = useSelector(authSelector);
  const dispatch = useDispatch();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const { actionSuccessColab, collaborators } = useSelector(collaboratorSelector);
  const [validatedCollaborators, setValidatedCollaborators] = useState([]);

  const typeOfStory = story?.story_details?.type_of_story;

  const options_role =
    typeOfStory === 'custom_event'
      ? [
          { id: 'admin', name: 'Admin' },
          { id: 'collaborator', name: 'Collaborator' },
          { id: 'viewer', name: 'Viewer' },
          { id: 'uga_collaborator', name: 'UGA Collaborator' },
        ]
      : [
          { id: 'admin', name: 'Admin' },
          { id: 'collaborator', name: 'Collaborator' },
          { id: 'viewer', name: 'Viewer' },
        ];

  const options_user =
    story.story_type === 'custom_event'
      ? [
          { id: 'other', name: 'Other' },
          { id: 'family', name: 'Family' },
          { id: 'friend', name: 'Friend' },
          { id: 'fan', name: 'Fan' },
          { id: 'athlete', name: 'Athlete' },
          { id: 'player', name: 'Player' },
        ]
      : [
          { id: 'other', name: 'Other' },
          { id: 'family', name: 'Family' },
          { id: 'friend', name: 'Friend' },
        ];

  const GetInitials = (firstName = '', lastName = '') => {
    const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : '';
    const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';
    return `${firstInitial}${lastInitial}`;
  };

  UseFirstRender(() => {
    if (story?.id) dispatch(getCollaboratorStory(story?.id));
  }, [story]);

  useEffect(() => {
    setValidatedCollaborators(collaborators?.collaborators?.filter((user: any) => user?.validated));
  }, [collaborators]);

  const handleRoleChange = (collabId: number, storyId: number, newRole: string) => {
    const updatedCollaborators = validatedCollaborators.map((collab: any) =>
      collab.user_id === collabId
        ? {
            ...collab,
            role: { ...collab.role, name: newRole },
          }
        : collab,
    );

    const payload = {
      story_id: storyId,
      collaborators: [
        {
          email: updatedCollaborators.find((c: any) => c.user_id == collabId)?.user?.email,
          type: updatedCollaborators.find((c: any) => c.user_id == collabId)?.user_type,
          role: newRole,
        },
      ],
    };
    dispatch(updateCollaborator(payload));
  };

  const handleUserTypeChange = (collabId: number, storyId: number, newUserType: string) => {
    const collabToUpdate: any = validatedCollaborators.find((c: any) => c.id === collabId);

    if (!collabToUpdate) return;

    const updatedCollaborators: any = validatedCollaborators.map((collab: any) =>
      collab.user_id === collabId ? { ...collab, user_type: newUserType } : collab,
    );
    setValidatedCollaborators(updatedCollaborators);

    const payload = {
      story_id: storyId,
      collaborators: [
        {
          email: collabToUpdate.user?.email,
          type: newUserType,
          role: collabToUpdate.role?.name,
        },
      ],
    };

    dispatch(updateCollaborator(payload));
  };
  const singularize = (word: any) => {
    return word.endsWith('s') ? word.slice(0, -1) : word;
  };
  return (
    <>
      {!actionSuccessColab ? (
        <Box
          width={'100%'}
          bgcolor={'#fff'}
          height={'100vh'}
          display={'flex'}
          justifyContent={'center'}
          alignItems={'center'}>
          <CircularProgress sx={{ color: palette.primary }} />
        </Box>
      ) : (
        <Box mt='0rem' width='100%' mx='auto' borderRadius='10px'>
          {validatedCollaborators &&
            validatedCollaborators.map((collab: any, index: any) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                  px: 1,
                  flexWrap: isMobile ? 'wrap' : 'nowrap',
                }}>
                {/* Avatar + Name + User Type */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                  <Avatar
                    sx={{
                      width: '36px',
                      height: '36px',
                      objectFit: 'cover',
                      fontSize: '0.75rem',
                      bgcolor: collab.color,
                      flexShrink: 0,
                    }}
                    src={collab?.user?.picture ? `${cdn_url}${collab?.user?.picture}` : undefined}>
                    {GetInitials(collab.user?.name, collab.user?.lastname)}
                  </Avatar>

                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {/* Name */}
                    <Typography
                      align='left'
                      fontSize='0.9rem'
                      fontWeight={500}
                      color='#000'
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '140px',
                      }}>
                      {collab.user?.name} {collab.user?.lastname}
                    </Typography>

                    {/* Relation */}
                    <MuiSelect
                      hideArrow
                      fontSize='0.8rem'
                      width={isMobile ? '100px' : '100px'}
                      disabled={collab?.user_id == user?.id}
                      value={singularize(collab?.user_type)}
                      handleSelect={(event: any) => handleUserTypeChange(collab.id, story.id, event.target.value)}
                      options={options_user.map((option) => ({
                        label: option.name,
                        value: option.id,
                      }))}
                      sx={{
                        textAlign: 'right',
                        fontSize: '0.8rem',
                      }}
                    />
                  </Box>
                </Box>

                {/* Role */}
                <Box sx={{ flexShrink: 0 }}>
                  <MuiSelect
                    hideArrow
                    fontSize='0.8rem'
                    width={isMobile ? '100px' : '100px'}
                    RightAlign={true}
                    disabled={collab?.user_id == user?.id}
                    value={collab.role?.name.replace('Story_', '') || 'collaborator'}
                    handleSelect={(event: any) =>
                      handleRoleChange(collab.user_id, story?.id, `Story_${event.target.value}`)
                    }
                    options={options_role.map((option) => ({
                      label: option.name,
                      value: option.name,
                    }))}
                    sx={{
                      position: 'relative',
                      left: '-22px',
                      textAlign: 'right',
                      fontSize: '0.8rem',
                    }}
                  />
                </Box>
              </Box>
            ))}

          {validatedCollaborators.length === 0 && (
            <Box width={'100%'} display={'flex'} my={2} justifyContent={'center'} alignItems={'center'}>
              <Typography fontSize={'0.8rem'} color='#000'>
                No Collaborators found for this memvy
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </>
  );
};
